import { useEffect, useState } from "react";
import type { FC, ReactNode } from "react";
import { Alert, Button, Col, Row } from "react-bootstrap";

import { OvertimeModal } from "components";
import { SolutionModal } from "features";

import type { AssignmentPracticeFragment } from "api/generated";

import { useAppDispatch, useAppSelector } from "store/hooks";
import { getLearningTime, measureLearningTime, startLearningTimer, resetLearningTimer } from "store/slices/timer";
import { setIsSoundMuted } from "store/slices/user";

import { eventEmitter, Events } from "utils/events/EventEmitter";
import { isMobile } from "utils/isMobile";

import IconCheck from "images/icons/check.svg";
import IconCross from "images/icons/close.svg";

import { useTranslation } from "react-i18next";
import { gtmTaskEvent } from "../../utils/gtmEvents";

interface Props {
  assignment: AssignmentPracticeFragment | undefined | null;
  children: ReactNode;
  type: "success" | "warning" | "danger" | undefined;
  index: number;
  toggleModals?: (index: number | null) => void;
  showBackOnSolution?: boolean;
  userAssignmentId: string;
  timeToLearn?: number;
  onNext?: () => void;
  addIcon?: boolean;
  subject?: string | null | undefined;
}

const AnswerPractice: FC<Props> = ({
  assignment,
  children,
  type,
  index,
  toggleModals,
  userAssignmentId,
  timeToLearn,
  onNext,
  addIcon = false,
  subject,
}) => {
  if (assignment === undefined || assignment === null) return null;

  const dispatch = useAppDispatch();
  const time = useAppSelector(getLearningTime);

  const [isModalVisible, setModalVisibility] = useState(false);
  const [isOvertimeModalVisible, setOvertimeModalVisibility] = useState(false);

  const { t } = useTranslation(["exercise"]);

  useEffect(() => {
    eventEmitter.dispatch(Events.ONBOARDING_UPDATE);
  }, []);

  useEffect(() => {
    if (isModalVisible || timeToLearn === undefined || onNext === undefined) return;

    dispatch(resetLearningTimer());
    dispatch(startLearningTimer());

    const interval = setInterval(() => {
      dispatch(measureLearningTime());
    }, 10000);

    return () => {
      clearInterval(interval);
      dispatch(resetLearningTimer());
    };
  }, [isModalVisible]);

  useEffect(() => {
    if (timeToLearn !== undefined && time >= timeToLearn) {
      setModalVisibility(false);
      setOvertimeModalVisibility(true);
    }
  }, [time]);

  const getIcon = () =>
    type === "success" ? (
      <IconCheck className={"text-success ms-4px"} />
    ) : (
      <IconCross className={"text-danger ms-4px"} />
    );

  const toggleModal = () => {
    if (toggleModals !== undefined) {
      toggleModals(isModalVisible ? null : index);
    }

    setModalVisibility(visibility => {
      dispatch(setIsSoundMuted(!visibility));
      return !visibility;
    });
  };

  useEffect(() => {
    gtmTaskEvent("task-result", {
      id: userAssignmentId,
      subject,
      score: -1,
    });
  }, []);

  useEffect(() => {
    if (isModalVisible) {
      gtmTaskEvent("task-analysis", {
        id: assignment.id,
        subject,
      });
    }
  }, [isModalVisible]);

  return (
    <>
      <Alert variant={type ?? "danger"} className="flex-grow-1">
        <Row className="flex-nowrap">
          <Col xs={"auto"} className={"d-flex align-items-center my-auto flex-grow-1 flex-shrink-1"}>
            {children}
            {addIcon && getIcon()}
          </Col>
          <Col xs={"auto"} className={"ms-auto my-auto d-flex align-items-center"}>
            <Button variant={"select"} className={"btn-shadow btn-smaller ms-4px"} onClick={toggleModal} id="analysis">
              {isMobile() ? t("btn.analysisMobile") : t("btn.analysisDesktop")}
            </Button>
          </Col>
        </Row>
      </Alert>

      <SolutionModal
        isShown={isModalVisible}
        assignment={assignment}
        questionIndex={index}
        answerType={type ?? "danger"}
        toggleModal={toggleModal}
        userAssignmentId={userAssignmentId}
      >
        {children}
      </SolutionModal>
      {isOvertimeModalVisible && onNext !== undefined ? (
        <OvertimeModal userAssignmentId={userAssignmentId} onClose={onNext} />
      ) : null}
    </>
  );
};

export default AnswerPractice;
